'use client'

import type { NestApi } from '@app/care/v1/video/adapters/getstream/videoServiceHttp'
import useApi from 'hooks/useApi'
import useThriveAuth from 'hooks/useThriveAuth'
import { useMemo, useRef } from 'react'

/**
 * Stable Nest + Care-token handles for video adapters.
 * useApi/useThriveAuth return new function identities every render; putting
 * those in useMemo deps recreates clients and restarts SSE → API storms.
 *
 * Also rejects non-OK use-http responses: use-http resolves with the body even
 * when `response.ok` is false and sets `request.error` — without this check,
 * ensureIdentity / resolveApiBase would cache a "successful" failed call.
 */
export function useStableVideoServiceParams(): {
  nestApi: NestApi
  getCareAccessToken: () => string | null | undefined
} {
  const api = useApi()
  const { getAccessTokenSilently } = useThriveAuth()

  const apiRef = useRef(api)
  const getTokenRef = useRef(getAccessTokenSilently)
  apiRef.current = api
  getTokenRef.current = getAccessTokenSilently

  return useMemo(
    () => ({
      nestApi: {
        get: (url: string) => apiRef.current.request.get(url),
        post: (url: string, body?: unknown) =>
          apiRef.current.request.post(url, body as never),
        getLastHttpResult: () => {
          const { request, response } = apiRef.current
          const status =
            typeof response?.status === 'number' ? response.status : 0
          const ok = !request.error && (response ? response.ok !== false : true)
          return {
            ok,
            status,
            errorMessage: request.error?.message,
          }
        },
      },
      getCareAccessToken: () => {
        try {
          return getTokenRef.current()
        } catch {
          return null
        }
      },
    }),
    []
  )
}
